import { Cpu, Sigma } from "lucide-react";
import { useMemo } from "react";
import { NumericInput } from "../../components/NumericInput";
import { copy } from "../../data/i18n";
import { decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { formatNumber } from "../../utils/numberFormat";
import { calculateWireMaterial } from "./formulas";
import { MaterialDropdown } from "./MaterialDropdown";
import { materialDensityPresets } from "./presets";
import type { WireMaterialInputs } from "./types";

type WireMaterialCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  inputs: WireMaterialInputs;
  inputText: Record<keyof WireMaterialInputs, string>;
  onInputTextChange: (key: keyof WireMaterialInputs, value: string) => void;
};

function displayLengthMeters(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value * 3.280839895;
}

function displayWeightKg(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value * 2.2046226218;
}

export function WireMaterialCalculator({
  language,
  unitSystem,
  inputs,
  inputText,
  onInputTextChange
}: WireMaterialCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";
  const result = useMemo(() => calculateWireMaterial(inputs), [inputs]);
  const lengthUnit = unitSystem === "metric" ? "m" : "ft";
  const spoolUnit = unitSystem === "metric" ? "kg" : "lb";
  const consumptionUnit = unitSystem === "metric" ? "g" : "lb";
  const selectedMaterial = materialDensityPresets.find((preset) => Math.abs(preset.density - inputs.density) < 0.005);

  return (
    <section className="calculator-panel wire-material-panel" aria-label={t.modules.wireMaterial}>
      <div className="calculator-heading">
        <div>
          <h2>{t.modules.wireMaterial}</h2>
          <span>{t.modules.wireMaterialDescription}</span>
        </div>
      </div>

      <div className="calculator-body wire-module-body">
        <div className="parameter-section wire-module-parameters">
          <div className="section-label">{t.inputParameters}</div>
          <div className="preset-panel wire-module-presets">
            <div className="section-label">{t.wireMaterial.material}</div>
            <MaterialDropdown
              label={t.wireMaterial.material}
              customLabel={t.wireMaterial.customMaterial}
              values={t.wireMaterial.materials}
              selectedKey={selectedMaterial?.key}
              onSelect={(density) => onInputTextChange("density", String(density))}
            />
          </div>
          <div className="input-grid">
            <NumericInput
              label={t.inputs.wireDiameter}
              shortLabel="Draht"
              unit={unitFor("wireDiameter", unitSystem)}
              value={toDisplayValue("wireDiameter", inputs.wireDiameter, unitSystem)}
              textValue={inputText.wireDiameter}
              decimals={decimalsFor("wireDiameter", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("wireDiameter", value)}
            />
            <NumericInput
              label={t.wireMaterial.density}
              shortLabel={t.wireMaterial.densityShort}
              unit="g/cm³"
              value={inputs.density}
              textValue={inputText.density}
              decimals={2}
              locale={locale}
              onChange={(value) => onInputTextChange("density", value)}
            />
            <NumericInput
              label={t.wireMaterial.spoolWeight}
              shortLabel={t.wireMaterial.spoolShort}
              unit={spoolUnit}
              value={displayWeightKg(inputs.spoolWeight, unitSystem)}
              textValue={inputText.spoolWeight}
              decimals={1}
              locale={locale}
              onChange={(value) => onInputTextChange("spoolWeight", value)}
            />
            <NumericInput
              label={t.inputs.wireFeed}
              shortLabel="vD"
              unit={unitFor("wireFeed", unitSystem)}
              value={toDisplayValue("wireFeed", inputs.wireFeed, unitSystem)}
              textValue={inputText.wireFeed}
              decimals={decimalsFor("wireFeed", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("wireFeed", value)}
            />
            <NumericInput
              label={t.wireMaterial.weldingTime}
              shortLabel={t.wireMaterial.weldingTime}
              unit="s"
              value={inputs.weldingTime}
              textValue={inputText.weldingTime}
              decimals={1}
              locale={locale}
              onChange={(value) => onInputTextChange("weldingTime", value)}
            />
          </div>
        </div>

        <aside className="result-panel wire-module-results" aria-live="polite">
          <div className="result-header">
            <span>
              <Cpu size={16} /> {t.result}
            </span>
          </div>
          <p className="result-label">{t.wireMaterial.rangeConsumption}</p>
          <div className="wire-main-results">
            <div>
              <span>{t.wireMaterial.wireConsumption}</span>
              <strong>
                {formatNumber(unitSystem === "metric" ? result.wireConsumption : result.wireConsumption / 453.59237, unitSystem === "metric" ? 1 : 3, locale)}
                <b>{consumptionUnit}</b>
              </strong>
            </div>
            <div>
              <span>{t.wireMaterial.maxWeldTime}</span>
              <strong>
                {formatNumber(result.maxWeldTimeMinutes, 1, locale)}
                <b>min</b>
              </strong>
            </div>
          </div>

          <div className="formula-box">
            <span>
              <Sigma size={15} /> {t.formula}
            </span>
            <code>Drahtlänge / vD</code>
          </div>

          <div className="detail-grid">
            <div>
              <span>{t.wireMaterial.wireLength}</span>
              <strong>{formatNumber(displayLengthMeters(result.wireLength, unitSystem), 0, locale)} {lengthUnit}</strong>
            </div>
            <div>
              <span>{t.wireMaterial.maxWeldTimeHours}</span>
              <strong>{formatNumber(result.maxWeldTimeMinutes / 60, 2, locale)} h</strong>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
